import { useState, type InputEvent, type SubmitEvent } from 'react'
import { sendPasswordResetEmail } from 'firebase/auth'
import { Link } from 'react-router'
import { authErrorMessage } from '../lib/authErrors'
import AuthCard from '../components/auth/AuthCard'
import AuthField from '../components/auth/AuthField'
import { auth } from '../lib/firebase'
import { collectErrors, type FieldErrors } from '../lib/formErrors'

const MESSAGES = { email: 'Enter a valid email address.' }

export default function ForgotPassword() {
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [error, setError] = useState('')
  const [sentTo, setSentTo] = useState('')
  const [submitting, setSubmitting] = useState(false)

  function clearFieldError(event: InputEvent<HTMLFormElement>) {
    const { name } = event.target as HTMLInputElement
    setFieldErrors((current) => ({ ...current, [name]: '' }))
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const invalid = collectErrors(form, MESSAGES)
    setFieldErrors(invalid)
    if (Object.keys(invalid).length > 0) return

    const email = String(new FormData(form).get('email'))
    setError('')
    setSubmitting(true)

    try {
      await sendPasswordResetEmail(auth, email)
      setSentTo(email)
    } catch (err) {
      setError(authErrorMessage(err))
      setSubmitting(false)
    }
  }

  return (
    <AuthCard
      title={sentTo ? 'Check your inbox' : 'Reset your password'}
      subtitle={
        sentTo
          ? `If ${sentTo} has an account, a link to set a new password is on its way.`
          : "Enter your email and we'll send you a link to set a new one."
      }
      footer={
        <Link to="/login" className="font-medium text-purple-700 hover:text-purple-800">
          ← Back to log in
        </Link>
      }
    >
      {!sentTo && (
        <form noValidate onSubmit={handleSubmit} onInput={clearFieldError} className="space-y-4">
          <AuthField
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="Your email"
            error={fieldErrors.email}
            required
          />

          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="h-11 w-full rounded-[10px] bg-linear-to-b from-stone-700 to-stone-950 text-[15px] font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(28,25,23,0.2)] transition-opacity enabled:hover:opacity-90 disabled:opacity-60"
          >
            {submitting ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
      )}
    </AuthCard>
  )
}
